import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { channelService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, MessageSquarePlus } from "lucide-react";
import WhatsAppConfigModal from "./WhatsAppConfigModal";

export const PROVIDER_TYPES = ["webchat", "email", "sms", "whatsapp", "instagram", "facebook", "voice"];
export const PROVIDER_COLOR = { webchat: "blue", email: "teal", sms: "amber", whatsapp: "green", instagram: "red", facebook: "blue", voice: "slate" };
const STATUSES = ["Draft", "Connected", "Disconnected"];
const EMPTY = { name: "", providerType: "webchat", direction: "both", tenantId: "" };

export default function ChannelsPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [waChannel, setWaChannel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await channelService.list(); setRows(d.items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      if (editing) { const { name, providerType, direction, status } = form; await channelService.update(editing.id, { name, providerType, direction, status }); toast.success("Channel updated"); }
      else { await channelService.create(form); toast.success("Channel created"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete channel "${r.name}"?`)) return;
    try { await channelService.remove(r.id); toast.success("Channel deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("CHANNEL.CREATE");
  const canEdit = hasPermission("CHANNEL.EDIT");
  const canDelete = hasPermission("CHANNEL.DELETE");

  const columns = [
    { key: "name", header: "Channel", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "providerType", header: "Provider", render: (r) => <Badge color={PROVIDER_COLOR[r.providerType] || "slate"}>{r.providerType}</Badge> },
    { key: "direction", header: "Direction", render: (r) => <span className="text-muted-foreground capitalize">{r.direction}</span> },
    { key: "status", header: "Status", render: (r) => <Badge color={r.status === "Connected" ? "green" : r.status === "Disconnected" ? "red" : "slate"}>{r.status}</Badge> },
    {
      key: "_a", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          {r.providerType === "whatsapp" && canEdit && (
            <button onClick={() => setWaChannel(r)} className="p-1.5 rounded-md hover:bg-green-500/10 text-green-600" title="Configure WhatsApp" data-testid={`channel-whatsapp-${r.id}`}><MessageSquarePlus className="h-4 w-4" /></button>
          )}
          {canEdit && <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`channel-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>}
          {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`channel-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="channels-page">
      <PageHeader title="Channels" subtitle="Provider-agnostic channel configuration · connect WhatsApp Business via the green icon"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="channel-create-btn">
            <Plus className="h-4 w-4" /> New Channel
          </button>
        )}
      />
      <DataTable columns={columns} rows={rows} loading={loading} testid="channels-table" empty="No channels yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="channel-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} Channel</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant *</label>
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="channel-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </div>
              )}
              <div><label className="block text-sm font-medium mb-1">Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="channel-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Provider</label><select value={form.providerType} onChange={(e) => setForm({ ...form, providerType: e.target.value })} className="ciq-input" data-testid="channel-provider">{PROVIDER_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}</select></div>
                <div><label className="block text-sm font-medium mb-1">Direction</label><select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className="ciq-input">{["both", "inbound", "outbound"].map((d) => <option key={d} value={d}>{d}</option>)}</select></div>
              </div>
              {editing && <div><label className="block text-sm font-medium mb-1">Status</label><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="ciq-input">{STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}</select></div>}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="channel-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
      {waChannel && <WhatsAppConfigModal channel={waChannel} onClose={() => setWaChannel(null)} onSaved={load} />}
    </div>
  );
}
